import { useContext, useEffect, useRef, useState } from 'react';
import { useLoaderData, useLocation, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import { GlobalContext } from '../../../library/Core/GlobalContext';
import CompanyLayout from './CompanyLayout';
import CompanyService from './CompanyService';

const ANNUAL_REVENUE_OPTIONS = [
    { value: '< ₹1 Crore',         label: '< ₹1 Crore — Early-stage / Bootstrapped' },
    { value: '₹1 – 10 Crore',      label: '₹1 – 10 Crore — Micro (MSME)'            },
    { value: '₹10 – 50 Crore',     label: '₹10 – 50 Crore — Small (lower band)'     },
    { value: '₹50 – 100 Crore',    label: '₹50 – 100 Crore — Small (upper band)'    },
    { value: '₹100 – 500 Crore',   label: '₹100 – 500 Crore — Medium (MSME)'        },
    { value: '₹500 – 1,000 Crore', label: '₹500 – 1,000 Crore — Large'              },
    { value: '> ₹1,000 Crore',     label: '> ₹1,000 Crore — Very Large / Enterprise' },
];

const EMPLOYEE_COUNT_DOMAIN = 'COMPANY_EMPLOYEE_COUNT';
const OPERATIONAL_STATUS_DOMAIN = 'COMPANY_OPERATIONAL_STATUS';
const COMPANY_AGE_DOMAIN = 'COMPANY_AGE';

const CURRENT_YEAR = new Date().getFullYear();

const companyAgeFromYear = (yearValue: unknown): number | null => {
    const year = Number(yearValue);
    if (!Number.isInteger(year) || year < 1900 || year > CURRENT_YEAR) return null;
    return CURRENT_YEAR - year;
};

const FLAG_FIELDS = [
    { key: 'fortune_five_hundred', label: 'Fortune 500' },
    { key: 'startup',              label: 'Startup' },
    { key: 'unicorn_status',       label: 'Unicorn Status' },
    { key: 'mnc_status',           label: 'MNC Status' },
    { key: 'listed_status',        label: 'Listed' },
    { key: 'is_defence',           label: 'Defence Company' },
    { key: 'is_prestigious',       label: 'Prestigious Company' },
    // { key: 'tier_one',             label: 'Tier 1' },
    { key: 'is_registered_company',       label: 'Registered Company' },
    { key: 'is_indian_government',        label: 'Indian Government' },
    { key: 'is_international_government', label: 'International Government' },
    { key: 'is_international_body',       label: 'International Body (UNO, World Bank, IMF etc)' },
    { key: 'is_law_firm',                 label: 'Law Firm' },
    { key: 'is_courts_tribunals',         label: 'Courts and Tribunals' },
    { key: 'is_educational_institute',    label: 'Educational Institute' },
    { key: 'is_healthcare_organization',  label: 'Hospital or Healthcare Organization' },
    { key: 'is_ngo',                      label: 'NGO' },
];

const DEFAULT_FORM = {
    name: '',
    logo: '',
    country: '',
    country_id: '',
    industry: '',
    industry_id: '',
    website: '',
    headquarters_country: '',
    company_size: '',
    fortune_five_hundred: 0,
    startup: 0,
    unicorn_status: 0,
    mnc_status: 0,
    listed_status: 0,
    is_defence: 0,
    is_prestigious: 0,
    // tier_one: 0,
    is_registered_company: 0,
    is_indian_government: 0,
    is_international_government: 0,
    is_international_body: 0,
    is_law_firm: 0,
    is_courts_tribunals: 0,
    is_educational_institute: 0,
    is_healthcare_organization: 0,
    is_ngo: 0,
    annual_revenue: '',
    profitable: 'No',
    balance_sheets: '',
    entrepreneurship_nature: '',
    establishment_year: '',
    operational_status: '',
};

const Company = () => {
    const loaderData = useLoaderData() as any;
    const navigate = useNavigate();
    const location = useLocation();
    const { company, routeInfo } = loaderData ?? {};
    const isNew = routeInfo?.id === 'new';

    useEffect(() => {
        window.scrollTo(0, 0);
    }, []);

    const { globalState } = useContext(GlobalContext);
    const countryDomain: any[] = (globalState as any)?.domain?.get?.('COUNTRY_CODE') ?? [];
    const industryDomain: any[] = (globalState as any)?.domain?.get?.('INDUSTRY_CODE') ?? [];
    const domainSelectOptions = (category: string): { value: string; label: string }[] =>
        ((globalState as any)?.domain?.get?.(category) ?? [])
            .slice()
            .sort((a: any, b: any) => (a.displayOrder ?? 0) - (b.displayOrder ?? 0))
            .map((d: any) => ({ value: String(d.code), label: String(d.value ?? '') }));
    const topCountries = countryDomain
        .filter((d: any) => d.displayOrder > 0)
        .sort((a: any, b: any) => a.displayOrder - b.displayOrder)
        .map((d: any) => ({ code: d.code, label: d.value }));
    const topCodes = new Set(topCountries.map((c) => c.code));
    const otherCountries = countryDomain
        .filter((d: any) => !topCodes.has(d.code))
        .map((d: any) => ({ code: d.code, label: d.value }));

    const [activeTab, setActiveTab] = useState<string>((location.state as any)?.initialTab ?? 'basic-info');
    const [form, setForm] = useState<any>({ ...DEFAULT_FORM, ...(company ?? {}) });
    const employeeCountOptions = (() => {
        const opts = domainSelectOptions(EMPLOYEE_COUNT_DOMAIN);
        const current = form.company_size;
        if (current && !opts.some((o) => o.value === String(current))) {
            return [...opts, { value: String(current), label: String(current) }];
        }
        return opts;
    })();
    const operationalStatusOptions = domainSelectOptions(OPERATIONAL_STATUS_DOMAIN);
    const companyAgeOptions = domainSelectOptions(COMPANY_AGE_DOMAIN);

    // If form.country is already a numeric code (new records), use it directly.
    // Otherwise find the code by name (old records that stored the display name).
    const selectedCountryCode = form.country && !isNaN(Number(form.country))
        ? String(form.country)
        : (countryDomain.find((d: any) => d.value === form.country)?.code ?? '');

    const selectedHQCode = countryDomain.find((d: any) => d.value === form.headquarters_country)?.code ?? '';

    const selectedIndustryCode = form.industry_id
        ? (industryDomain.find((d: any) => String(d.code) === String(form.industry_id))?.code ?? '')
        : (industryDomain.find((d: any) => d.value === form.industry)?.code ?? '');
    const [logoUploading, setLogoUploading] = useState(false);
    const [sheetUploading, setSheetUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const companyAge = companyAgeFromYear(form.establishment_year);
    const companyAgeBandCode =
        companyAge == null
            ? ''
            : companyAge < 2
              ? '<2 years'
              : companyAge <= 5
                ? '2-5 Years'
                : companyAge <= 10
                  ? '6-10 years'
                  : '10+ years';
    const companyAgeBandLabel =
        companyAgeOptions.find((o) => o.value === companyAgeBandCode)?.label || companyAgeBandCode;

    const logoInputRef = useRef<HTMLInputElement>(null);
    const sheetInputRef = useRef<HTMLInputElement>(null);

    const setField = (key: string, value: any) => {
        setForm((prev: any) => ({ ...prev, [key]: value }));
        if (errors[key]) setErrors((prev) => ({ ...prev, [key]: '' }));
    };

    const handleIndustryChange = (code: string) => {
        const item = industryDomain.find((d: any) => d.code === code);
        const numericId = parseInt(code);
        setForm((prev: any) => ({
            ...prev,
            industry: item?.value ?? '',
            industry_id: !isNaN(numericId) ? numericId : null,
        }));
    };

    const handleCountryChange = (code: string) => {
        setForm((prev: any) => ({
            ...prev,
            country: code,       // store numeric code e.g. "100"
            country_id: null,    // not used
        }));
    };

    const handleLogoUpload = async (file: File) => {
        const ext = file.name.split('.').at(-1)?.toLowerCase() ?? '';
        if (!['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext)) {
            Swal.fire({ icon: 'error', title: 'Invalid File', text: 'Please upload an image (jpg, jpeg, png, gif, webp).' });
            return;
        }
        setLogoUploading(true);
        const url = await CompanyService.uploadFile(file);
        setLogoUploading(false);
        if (url) {
            setField('logo', url);
            setErrors((prev) => ({ ...prev, logo: '' }));
        } else {
            Swal.fire({ icon: 'error', title: 'Upload Failed', text: 'Could not upload logo. Please try again.' });
        }
    };

    const handleSheetUpload = async (file: File) => {
        const ext = file.name.split('.').at(-1)?.toLowerCase() ?? '';
        if (!['xlsx', 'xls'].includes(ext)) {
            Swal.fire({ icon: 'error', title: 'Invalid File', text: 'Only Excel files (.xlsx, .xls) are allowed.' });
            if (sheetInputRef.current) sheetInputRef.current.value = '';
            return;
        }
        const sizeMB = file.size / (1024 * 1024);
        if (sizeMB > 25) {
            Swal.fire({ icon: 'error', title: 'File Too Large', text: 'Balance sheet file must be under 25 MB.' });
            if (sheetInputRef.current) sheetInputRef.current.value = '';
            return;
        }
        setSheetUploading(true);
        const url = await CompanyService.uploadFile(file);
        setSheetUploading(false);
        if (url) {
            setField('balance_sheets', url);
        } else {
            Swal.fire({ icon: 'error', title: 'Upload Failed', text: 'Could not upload balance sheet to S3. Please try again.' });
            if (sheetInputRef.current) sheetInputRef.current.value = '';
        }
    };

    const validate = () => {
        const errs: Record<string, string> = {};
        if (!form.name?.trim()) errs.name = 'Company name is required.';
        if (!form.logo?.trim()) errs.logo = 'Company logo is required.';
        const yearRaw = String(form.establishment_year ?? '').trim();
        if (yearRaw) {
            const year = Number(yearRaw);
            if (!/^\d{4}$/.test(yearRaw) || year < 1900 || year > CURRENT_YEAR) {
                errs.establishment_year = `Enter a 4-digit year between 1900 and ${CURRENT_YEAR}.`;
            }
        }
        return errs;
    };

    const handleSave = async () => {
        const validationErrors = validate();
        if (Object.keys(validationErrors).length > 0) {
            setErrors(validationErrors);
            Swal.fire({ icon: 'warning', title: 'Validation Error', text: 'Please fill in all required fields.' });
            setActiveTab('basic-info');
            return;
        }

        setSaving(true);
        try {
            const payload = {
                ...form,
                operational_status: form.operational_status || null,
                company_size: form.company_size || null,
                establishment_year: form.establishment_year || null,
            };
            let response: any;
            if (isNew) {
                response = await CompanyService.create(payload);
            } else {
                response = await CompanyService.update(company.id, payload);
            }
            const data = response?.data ?? response;

            if (data?.success === false) {
                Swal.fire({ icon: 'error', title: 'Error', text: data.message ?? 'Something went wrong.' });
            } else {
                await Swal.fire({
                    icon: 'success',
                    title: 'Saved!',
                    text: isNew ? 'Company added successfully.' : 'Company updated successfully.',
                    timer: 1500,
                    showConfirmButton: false,
                    timerProgressBar: true,
                });
                if (isNew) {
                    const insertId = data?.data?.insertId;
                    navigate(
                        insertId ? `/company/${insertId}/basic-info` : '/company/listing',
                        insertId ? { state: { initialTab: 'balance-sheets' } } : undefined
                    );
                }
            }
        } catch (err: any) {
            const msg = err?.response?.data?.message || err?.message || 'Failed to save company.';
            Swal.fire({ icon: 'error', title: 'Error', text: msg });
        } finally {
            setSaving(false);
        }
    };

    return (
        <CompanyLayout isNew={isNew} activeTab={activeTab} onTabChange={setActiveTab}>

            {/* ── Basic Info Tab ── */}
            <div className={`tab-pane fade ${activeTab === 'basic-info' ? 'show active' : ''}`} role="tabpanel">
                <div className="lstCustForm w-100 formrowpx mt-2 mx-auto p-4">
                    <div className="row g-4">

                        <div className="col-md-6">
                            <label className="form-label font-14 font-500">
                                Company Name <span className="text-danger">*</span>
                            </label>
                            <input
                                type="text"
                                className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                                placeholder="Enter official company name"
                                value={form.name ?? ''}
                                onChange={(e) => setField('name', e.target.value)}
                            />
                            {errors.name && <div className="invalid-feedback">{errors.name}</div>}
                        </div>

                        <div className="col-md-6">
                            <label className="form-label font-14 font-500">Website</label>
                            <input
                                type="text"
                                className="form-control"
                                placeholder="https://www.example.com"
                                value={form.website ?? ''}
                                onChange={(e) => setField('website', e.target.value)}
                            />
                        </div>

                        <div className="col-md-6">
                            <label className="form-label font-14 font-500">Country</label>
                            <select
                                className="form-select"
                                value={selectedCountryCode}
                                onChange={(e) => handleCountryChange(e.target.value)}>
                                <option value="">— Select Country —</option>
                                {topCountries.length > 0 && (
                                    <optgroup label="Top Countries">
                                        {topCountries.map((c) => (
                                            <option key={`top-${c.code}`} value={c.code}>{c.label}</option>
                                        ))}
                                    </optgroup>
                                )}
                                <optgroup label="All Countries">
                                    {otherCountries.map((c) => (
                                        <option key={c.code} value={c.code}>{c.label}</option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label font-14 font-500">Headquarters Country</label>
                            <select
                                className="form-select"
                                value={selectedHQCode}
                                onChange={(e) => {
                                    const item = countryDomain.find((d: any) => d.code === e.target.value);
                                    setField('headquarters_country', item?.value ?? '');
                                }}>
                                <option value="">— Select Country —</option>
                                {topCountries.length > 0 && (
                                    <optgroup label="Top Countries">
                                        {topCountries.map((c) => (
                                            <option key={`top-${c.code}`} value={c.code}>{c.label}</option>
                                        ))}
                                    </optgroup>
                                )}
                                <optgroup label="All Countries">
                                    {otherCountries.map((c) => (
                                        <option key={c.code} value={c.code}>{c.label}</option>
                                    ))}
                                </optgroup>
                            </select>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label font-14 font-500">Industry</label>
                            <select
                                className="form-select"
                                value={selectedIndustryCode}
                                onChange={(e) => handleIndustryChange(e.target.value)}>
                                <option value="">— Select Industry —</option>
                                {industryDomain.map((d: any) => (
                                    <option key={d.code} value={d.code}>{d.value}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label font-14 font-500">Employee Count</label>
                            <select
                                className="form-select"
                                value={form.company_size ?? ''}
                                onChange={(e) => setField('company_size', e.target.value)}>
                                <option value="">— Select Employee Count —</option>
                                {employeeCountOptions.map((opt) => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </select>
                        </div>

                        <div className="col-md-6">
                            <label className="form-label font-14 font-500">Establishment Year</label>
                            <input
                                type="text"
                                inputMode="numeric"
                                maxLength={4}
                                className={`form-control ${errors.establishment_year ? 'is-invalid' : ''}`}
                                placeholder="e.g. 2014"
                                value={form.establishment_year ?? ''}
                                onChange={(e) => setField('establishment_year', e.target.value.replace(/\D/g, '').slice(0, 4))}
                            />
                            {errors.establishment_year ? (
                                <div className="invalid-feedback">{errors.establishment_year}</div>
                            ) : (
                                <div className="form-text font-12">
                                    {companyAge == null
                                        ? 'Optional 4-digit year. Company age is current year minus this value.'
                                        : `Company age: ${companyAge === 1 ? '1 year' : `${companyAge} years`} (${companyAgeBandLabel})`}
                                </div>
                            )}
                        </div>

                        {/* Logo Upload */}
                        <div className="col-md-12">
                            <label className="form-label font-14 font-500">
                                Company Logo <span className="text-danger">*</span>
                            </label>
                            <div
                                className={`upload_section ${errors.logo ? 'border border-danger' : ''}`}
                                onClick={() => logoInputRef.current?.click()}
                                style={{ cursor: 'pointer' }}>
                                <input
                                    ref={logoInputRef}
                                    type="file"
                                    accept=".jpg,.jpeg,.png,.gif,.webp"
                                    className="d-none"
                                    onChange={(e) => {
                                        if (e.target.files?.[0]) handleLogoUpload(e.target.files[0]);
                                        e.target.value = '';
                                    }}
                                />
                                <div className="row g-2 align-items-center justify-content-center">
                                    <div className="col-sm d-flex align-items-center justify-content-center">
                                        <div className="cloud-wrap px-3 px-sm-5 mx-auto">
                                            <div className="cloud-img">
                                                <img src={`${process.env.REACT_APP_IMAGE_BASEPATH}/upload.svg`} alt="upload" />
                                            </div>
                                            <div className="cloud-text">
                                                {logoUploading ? (
                                                    <span>Uploading...</span>
                                                ) : (
                                                    <span>Drag and drop image here or <u className="text-sky-blue">Browse</u></span>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                            <div className="d-flex justify-content-between align-items-center">
                                {errors.logo
                                    ? <span className="text-danger font-13">{errors.logo}</span>
                                    : <span />
                                }
                                <span className="font-14 text-dark-blue opacity-7">Only jpg, jpeg, png, gif, webp files.</span>
                            </div>
                            {form.logo && (
                                <div className="mt-2 d-flex align-items-center gap-3">
                                    <img
                                        src={form.logo}
                                        alt="Company Logo Preview"
                                        style={{ width: 90, height: 90, objectFit: 'contain', border: '1px solid #e0e0e0', borderRadius: 8, padding: 6, background: '#fff' }}
                                    />
                                    <div>
                                        <p className="mb-1 font-13 text-muted">Logo preview</p>
                                        <button type="button" className="btn btn-sm btn-outline-danger" onClick={() => setField('logo', '')}>
                                            Remove Logo
                                        </button>
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Company Attribute Flags */}
                        <div className="col-md-12">
                            <div className="border-top pt-4 mt-2">
                                <h5 className="font-18 font-600 mb-1">Company Attributes</h5>
                                <p className="text-muted font-13 mb-3">Select Yes or No for each attribute that applies to this company.</p>
                                <div className="row g-3">
                                    {FLAG_FIELDS.map(({ key, label }) => (
                                        <div className="col-md-3 col-sm-6" key={key}>
                                            <div className="p-3 rounded" style={{ border: '1px solid #e8e8e8', background: '#fafafa' }}>
                                                <label className="form-label font-14 font-500 d-block mb-2">{label}</label>
                                                <div className="d-flex gap-4">
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="radio" name={`flag_${key}`} id={`${key}_yes`}
                                                            checked={Number(form[key]) === 1} onChange={() => setField(key, 1)} />
                                                        <label className="form-check-label font-14" htmlFor={`${key}_yes`}>Yes</label>
                                                    </div>
                                                    <div className="form-check">
                                                        <input className="form-check-input" type="radio" name={`flag_${key}`} id={`${key}_no`}
                                                            checked={Number(form[key]) === 0}
                                                            onChange={() => {
                                                                setField(key, 0);
                                                                if (key === 'startup') setField('entrepreneurship_nature', '');
                                                            }} />
                                                        <label className="form-check-label font-14" htmlFor={`${key}_no`}>No</label>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div className="col-md-12 d-flex justify-content-end gap-3 mt-2">
                            <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/company/listing')}>Cancel</button>
                            <button type="button" className="btn btn-green" onClick={handleSave} disabled={saving}>
                               <span> {saving ? 'Saving...' : isNew ? 'Add Company' : 'Save Changes'}</span>
                            </button>
                        </div>
                    </div>
                </div>
            </div>

            {/* ── Balance Sheets Tab ── */}
            <div className={`tab-pane fade ${activeTab === 'balance-sheets' ? 'show active' : ''}`} role="tabpanel">
                <div className="lstCustForm w-100 formrowpx mt-2 mx-auto p-4">
                    <div className="row g-4">
                        <div className="col-md-12">
                            <h5 className="font-18 font-600 mb-1">Balance Sheet</h5>
                            <p className="text-muted font-13 mb-4">
                                Upload company balance sheet in Excel format (.xlsx / .xls). Maximum file size is 25 MB.
                            </p>

                            <div className="p-4 rounded" style={{ border: '1px solid #e0e0e0', background: '#f8f9fa' }}>
                                <div className="row g-3">

                                    {/* Operational Status */}
                                    <div className="col-md-12">
                                        <label className="form-label font-14 font-500">Operational Status</label>
                                        <select
                                            className="form-select"
                                            value={form.operational_status ?? ''}
                                            onChange={(e) => setField('operational_status', e.target.value)}>
                                            <option value="">— Select Operational Status —</option>
                                            {operationalStatusOptions.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Annual Revenue */}
                                    <div className="col-md-12">
                                        <label className="form-label font-14 font-500">Annual Revenue</label>
                                        <select
                                            className="form-select"
                                            value={form.annual_revenue ?? ''}
                                            onChange={(e) => setField('annual_revenue', e.target.value)}>
                                            <option value="">— Select Annual Revenue —</option>
                                            {ANNUAL_REVENUE_OPTIONS.map((opt) => (
                                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                                            ))}
                                        </select>
                                    </div>

                                    {/* Profitable */}
                                    <div className="col-md-12">
                                        <div className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="checkbox"
                                                id="field_profitable"
                                                checked={form.profitable === 'Yes'}
                                                onChange={(e) => setField('profitable', e.target.checked ? 'Yes' : 'No')}
                                            />
                                            <label className="form-check-label font-14 font-500" htmlFor="field_profitable">
                                                Profitable
                                            </label>
                                        </div>
                                    </div>

                                    {Number(form.startup) === 1 && (
                                        <div className="col-md-12">
                                            <label className="form-label font-14 font-500 d-block">Nature of Entrepreneurship</label>
                                            <div className="d-flex gap-4 flex-wrap">
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="entrepreneurship_nature"
                                                        id="nature_first_gen"
                                                        checked={form.entrepreneurship_nature === 'FIRST_GENERATION'}
                                                        onChange={() => setField('entrepreneurship_nature', 'FIRST_GENERATION')}
                                                    />
                                                    <label className="form-check-label font-14" htmlFor="nature_first_gen">
                                                        1st Generation Entrepreneur
                                                    </label>
                                                </div>
                                                <div className="form-check">
                                                    <input
                                                        className="form-check-input"
                                                        type="radio"
                                                        name="entrepreneurship_nature"
                                                        id="nature_family"
                                                        checked={form.entrepreneurship_nature === 'FAMILY_MANAGED'}
                                                        onChange={() => setField('entrepreneurship_nature', 'FAMILY_MANAGED')}
                                                    />
                                                    <label className="form-check-label font-14" htmlFor="nature_family">
                                                        Family Managed Business
                                                    </label>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Excel Upload */}
                                    <div className="col-md-12">
                                        <label className="form-label font-14 font-500">
                                            Balance Sheet File
                                            <span className="text-muted font-12 ms-1">(Excel, max 25 MB)</span>
                                        </label>
                                        <div
                                            className="upload_section"
                                            onClick={() => !sheetUploading && sheetInputRef.current?.click()}
                                            style={{ cursor: sheetUploading ? 'wait' : 'pointer', minHeight: 70 }}>
                                            <input
                                                ref={sheetInputRef}
                                                type="file"
                                                accept=".xlsx,.xls"
                                                className="d-none"
                                                onChange={(e) => {
                                                    if (e.target.files?.[0]) handleSheetUpload(e.target.files[0]);
                                                }}
                                            />
                                            <div className="d-flex align-items-center justify-content-center py-3">
                                                {sheetUploading ? (
                                                    <span className="font-13">⏳ Uploading to S3...</span>
                                                ) : form.balance_sheets ? (
                                                    <span className="font-13 text-success">
                                                        ✓ File uploaded.{' '}
                                                        <u className="text-sky-blue" onClick={(e) => { e.stopPropagation(); window.open(form.balance_sheets, '_blank'); }}>
                                                            Preview
                                                        </u>{' '}|{' '}
                                                        <u className="text-danger" onClick={(e) => { e.stopPropagation(); setField('balance_sheets', ''); if (sheetInputRef.current) sheetInputRef.current.value = ''; }}>
                                                            Remove
                                                        </u>
                                                    </span>
                                                ) : (
                                                    <span className="font-13">Click to upload .xlsx / .xls file</span>
                                                )}
                                            </div>
                                        </div>
                                        <div className="text-end font-12 text-dark-blue opacity-7">Only .xlsx / .xls, max 25 MB.</div>
                                    </div>

                                </div>
                            </div>

                            <div className="d-flex justify-content-end gap-3 mt-4">
                                <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/company/listing')}>Cancel</button>
                                <button type="button" className="btn btn-green" onClick={handleSave} disabled={saving}>
                                    <span>{saving ? 'Saving...' : isNew ? 'Add Company' : 'Save Changes'}</span>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

        </CompanyLayout>
    );
};

export default Company;
